import React, { useState, useCallback, useEffect } from 'react';
import { ChatWindow } from './components/ChatWindow';
import { ChatInput } from './components/ChatInput';
import { useChatApi } from './hooks/useChatApi';
import { useChatSession } from './hooks/useChatSession';
import { ChatMessage } from './types/chat.types';
import { FirebaseUserProps } from '../../common/entityUtilities';
import { useTranslation } from 'react-i18next';
import './styles/ChatWidget.css';

interface ChatWidgetProps {
  firebaseUser?: FirebaseUserProps;
  userId?: string;
  context?: {
    page?: string;
    userAgent?: string;
    metadata?: Record<string, any>;
  };
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ firebaseUser, userId, context }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [showPopup, setShowPopup] = useState(true);
  const [hasAddedGreeting, setHasAddedGreeting] = useState(false);

  const { sendMessage, sendFeedback, loading, error } = useChatApi(firebaseUser);
  const { sessionId, messages, addMessage, resetSession, loadMessages } = useChatSession();

  useEffect(() => {
    // Show unread indicator when bot sends a message and chat is closed
    if (!isOpen && messages.length > 0 && messages[messages.length - 1].role === 'assistant') {
      setHasUnread(true);
    }
  }, [messages, isOpen]);

  useEffect(() => {
    // Add initial greeting message when chat is opened for the first time
    if (isOpen && !hasAddedGreeting && messages.length === 0 && sessionId) {
      const greetingMessage: ChatMessage = {
        id: `greeting-${Date.now()}`,
        role: 'assistant',
        message: t('greeting', {ns: 'chatbot'}),
        timestamp: new Date().toISOString(),
      };
      addMessage(greetingMessage);
      setHasAddedGreeting(true);
    }
  }, [isOpen, hasAddedGreeting, messages.length, sessionId, addMessage]);

  useEffect(() => {
    // Auto-dismiss popup after 5 seconds
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setHasUnread(false);
    }
  };

  const handleDismissPopup = () => {
    setShowPopup(false);
  };

  const handleSendMessage = useCallback(async (messageText: string) => {
    if (!sessionId) {
      console.error('Session ID not initialized');
      return;
    }

    // Add user message to UI immediately
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      message: messageText,
      timestamp: new Date().toISOString(),
    };
    addMessage(userMessage);

    // Send to API
    const response = await sendMessage({
      message: messageText,
      sessionId,
      userId,
      context,
    });

    if (response) {
      addMessage(response);
    }
  }, [sessionId, userId, context, sendMessage, addMessage]);

  const handleFeedback = useCallback(async (
    messageId: string,
    feedback: 'positive' | 'negative'
  ) => {
    if (sessionId) {
      await sendFeedback(messageId, sessionId, feedback);
    }
  }, [sessionId, sendFeedback]);

  const handleReset = () => {
    if (window.confirm('Are you sure you want to start a new conversation?')) {
      resetSession();
    }
  };

  return (
    <div className="chat-widget">
      {/* Dismissable Popup Message */}
      {showPopup && !isOpen && (
        <div className="chat-widget-popup">
          <button
            className="chat-widget-popup-close"
            onClick={handleDismissPopup}
            aria-label="Dismiss message"
          >
            ×
          </button>
          <p>{t('teaser', {ns: 'chatbot'})}</p>
        </div>
      )}

      {/* Chat Toggle Button */}
      <button
        className={`chat-widget-toggle ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        aria-label="Toggle chat"
      >
        {isOpen ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            width="24"
            height="24"
          >
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        ) : (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              width="24"
              height="24"
            >
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
            </svg>
            {hasUnread && <span className="chat-widget-unread-badge"></span>}
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-widget-window">
          <div className="chat-widget-header">
            <div className="chat-widget-header-title">
              <h3>Cribsy Assistant</h3>
              <span className="chat-widget-header-status">Online</span>
            </div>
            <button
              className="chat-widget-reset-btn"
              onClick={handleReset}
              title="Start new conversation"
            >
              ↻
            </button>
          </div>

          {error && (
            <div className="chat-widget-error">
              <strong>Error:</strong> {error.message}
              {error.retryAfter && (
                <span> (Retry after {error.retryAfter} seconds)</span>
              )}
            </div>
          )}

          <div className="chat-widget-privacy-warning">
            <strong>⚠️ {t('privacy_warning_title', {ns: 'chatbot'})}</strong>
            <p>{t('privacy_warning', {ns: 'chatbot'})}</p>
          </div>

          <ChatWindow
            messages={messages}
            isLoading={loading}
            onFeedback={handleFeedback}
          />

          <ChatInput
            onSendMessage={handleSendMessage}
            disabled={loading}
            placeholder={t('placeholder', {ns: 'chatbot'})}
          />
        </div>
      )}
    </div>
  );
};
